import { useMemo, useState } from 'react';
import { useTranslation } from '@sib/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Checkbox,
} from '@sib/ui';
import { Tag, X } from '@sib/ui/icons';

interface BulkEditTagsDialogOption {
  uuid: string;
  name: string;
  color?: string;
}

export interface BulkEditTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: BulkEditTagsDialogOption[];
  onBulkDelete: (uuids: string[]) => void;
  isDeleting?: boolean;
}

export const BulkEditTagsDialog = ({
  open,
  onOpenChange,
  tags,
  onBulkDelete,
  isDeleting = false,
}: BulkEditTagsDialogProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>([]);

  const selectedTagIds = useMemo(
    () => new Set(tags.map(tag => tag.uuid)),
    [tags]
  );

  const validSelected = useMemo(
    () => selected.filter(id => selectedTagIds.has(id)),
    [selected, selectedTagIds]
  );

  const handleToggle = (uuid: string) => {
    setSelected(prev =>
      prev.includes(uuid) ? prev.filter(id => id !== uuid) : [...prev, uuid]
    );
  };

  const handleDelete = () => {
    if (validSelected.length === 0) return;

    onBulkDelete(validSelected);
    setSelected([]);
    onOpenChange(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) setSelected([]);
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="flex flex-row justify-between">
          <DialogTitle>
            {t('components.taskForm.fields.tag.editTagsDialogTitle')}
          </DialogTitle>
          <DialogClose>
            <X className="mb-1 size-4 cursor-pointer" />
          </DialogClose>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
            {tags.map(tag => (
              <label
                key={`bulk-edit-tags-dialog-${tag.uuid}`}
                className="hover:bg-muted flex cursor-pointer items-center gap-2.5 rounded px-2 py-1"
              >
                <Checkbox
                  checked={validSelected.includes(tag.uuid)}
                  onCheckedChange={() => handleToggle(tag.uuid)}
                  className="text-primary background-primary size-5"
                  outlined
                />
                <Tag style={{ color: tag.color }} />
                <span className="text-primary text-medium">{tag.name}</span>
              </label>
            ))}
          </div>
          <Button
            variant="outline"
            className="w-full"
            disabled={validSelected.length === 0 || isDeleting}
            onClick={handleDelete}
          >
            {t('components.taskForm.fields.tag.deleteSelectedTags', {
              selectedCount: validSelected.length,
            })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
